import { useEffect, useState } from 'react'

import { t } from 'commons/libs/i18n'
import { formatDate } from 'commons/utils/date'
import {
  ActionTabs,
  Badge,
  Flex,
  type HeadItemType,
  type ItemActionTabsType,
  LoadingHavaianas,
  Modal,
  NotFound,
  PaginationV2,
  Table,
  Text,
} from 'components'
import { TabsWrapper } from 'container/SalesCalendar/styles'
import type { Office } from 'services/shared'
import {
  getSalesCalendarTracking,
  type SalesCalendarListItemUI,
  setTrackingPage,
  setTrackingPageSize,
} from 'store/SalesCalendarContext/salesCalendarSlice'
import { useDispatch, useSelector } from 'store/store'

import * as S from './styles'

export type CalendarTrackingModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
  offices: Office[]
  defaultOfficeIndex: string
  checkedCalendars: SalesCalendarListItemUI[]
}

export const CalendarTrackingModal = ({
  open,
  setOpen,
  offices,
  defaultOfficeIndex,
  checkedCalendars,
}: CalendarTrackingModalProps) => {
  const dispatch = useDispatch()
  const [selectedOfficeIndex, setSelectedOfficeIndex] = useState<string>(defaultOfficeIndex)
  const { trackingItems, loadingTracking, trackingPagination } = useSelector(
    (state) => state.salesCalendar,
  )
  const { currentPage, pageSize, totalItems } = trackingPagination

  const selectedOffice = offices[+selectedOfficeIndex]?.id ?? ''

  const trackingOfficeTabs: ItemActionTabsType[] = offices.map((office, index) => ({
    label: office.id,
    onClick: () => {
      setSelectedOfficeIndex(index.toString())
      dispatch(setTrackingPage(1))
    },
  }))

  const handleClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    if (selectedOffice && checkedCalendars.length > 0) {
      const yearMonthList = checkedCalendars.map((c) => c.yearMonth)
      dispatch(getSalesCalendarTracking(selectedOffice, yearMonthList, currentPage, pageSize))
    }
  }, [selectedOffice, currentPage, pageSize, checkedCalendars])

  const tableLabels: HeadItemType[] = [
    { columnName: 'date', label: t('components.trackingModal.date') },
    { columnName: 'time', label: t('components.trackingModal.time') },
    { columnName: 'user', label: t('components.trackingModal.user') },
    { columnName: 'fairShare', label: t('screens.salesCalendar.fairShare') },
    { columnName: 'action', label: t('components.trackingModal.action') },
    { columnName: 'description', label: t('components.trackingModal.description') },
  ]

  const tableData = () =>
    trackingItems.map((item, index) => ({
      content: [
        item.formattedDate,
        item.formattedTime,
        item.username,
        item.formattedYearMonth,
        item.action,
        item.description,
      ],
      id: `${item.officeCode}-${item.year}-${item.month}-${index}`,
    }))

  return (
    <Modal
      closeAction={handleClose}
      modalWidth={1092}
      open={open}
      returnAction={handleClose}
      setOpen={setOpen}
      showAction={false}
      showClose
      showReturn={false}
      title={
        <Flex direction="column" gap="sm">
          <Text color="stronger" size="md" weight="md">
            {t('components.trackingModal.title')}
          </Text>
          <Text size="xs">{t('components.trackingModal.trackingDescription')}</Text>
        </Flex>
      }
      titleSize={18}
    >
      <Flex direction="column" fullWidth gap="md">
        <S.BadgesWrapper>
          <Text size="2xs">{t('screens.salesCalendar.selectedPeriods')}</Text>
          {checkedCalendars.map((calendar) => (
            <Badge appearance="soft" color="info" key={calendar.yearMonth}>
              {formatDate(String(calendar.yearMonth), 'MM/YYYY', 'YYYYMM')}
            </Badge>
          ))}
        </S.BadgesWrapper>

        <TabsWrapper>
          <ActionTabs currentItem={selectedOfficeIndex} items={trackingOfficeTabs} />
        </TabsWrapper>

        {loadingTracking ? (
          <LoadingHavaianas />
        ) : (
          <>
            <S.TableWrapper>
              {trackingItems.length ? (
                <Table
                  bodyData={tableData()}
                  checkboxSize="small"
                  distributeWidth={true}
                  headData={tableLabels}
                  rowHeight={48}
                />
              ) : (
                <NotFound
                  description={t('screens.salesCalendar.noChangesDescription')}
                  title={t('screens.salesCalendar.noChanges')}
                />
              )}
            </S.TableWrapper>
            <PaginationV2
              currentPage={currentPage}
              itemsPerPage={pageSize}
              itemsPerPageOptions={[10, 20, 30, 40, 50]}
              onItemsPerPageChange={(size) => dispatch(setTrackingPageSize(size))}
              onPageChange={(page) => dispatch(setTrackingPage(page))}
              totalItems={totalItems}
            />
          </>
        )}
      </Flex>
    </Modal>
  )
}
